import React, { useMemo, useState } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { DEMO_EVIDENCE_EVENTS, SITE_MAP } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';
import ViewModeToggle from '../components/ViewModeToggle';
import { useViewMode } from '../components/ViewModeContext';
import { EvidenceMonitorPanel } from '../components/MonitorOverlay';

const EVENT_COST_MAP: Record<string, Record<string, number>> = {
  'Vital Signs':  { UCLA: 45, UCSF: 48, 'Johns Hopkins': 46 },
  'Lab Result':   { UCLA: 285, UCSF: 295, 'Johns Hopkins': 290 },
  'AE Report':    { UCLA: 45, UCSF: 48, 'Johns Hopkins': 46 },
  'SAE Report':   { UCLA: 45, UCSF: 48, 'Johns Hopkins': 46 },
  'PK Sample':    { UCLA: 55, UCSF: 58, 'Johns Hopkins': 56 },
  'ECG':          { UCLA: 125, UCSF: 132, 'Johns Hopkins': 128 },
};

function getEventCost(eventType: string, siteLabel: string): number {
  return EVENT_COST_MAP[eventType]?.[siteLabel] ?? 0;
}

const EVIDENCE_GLOSSARY: GlossaryEntry[] = [
  { term: 'ev-', definition: 'Evidence event identifier' },
  { term: 'Effective Timestamp', definition: 'When the clinical event actually occurred' },
  { term: 'Knowledge Timestamp', definition: 'When it was recorded in the system' },
  { term: 'SOA', definition: 'Schedule of Assessments — protocol document defining all procedures per visit' },
  { term: 'Query', definition: 'Question raised by monitor when data appears incorrect or missing' },
  { term: 'Source', definition: 'Original record that EDC data was transcribed from' },
  { term: 'Monitor', definition: 'Independent person who verifies site data against source records' },
  { term: 'Coordinator view', definition: 'Data entry and collection workflow for site staff' },
  { term: 'Monitor view', definition: 'Verification and SDV workflow for clinical monitors' },
  { term: 'Delay', definition: 'Gap between effective and knowledge timestamps' },
  { term: 'AE Report', definition: 'Adverse Event \u2014 any untoward medical occurrence after dosing' },
  { term: 'SAE Report', definition: 'Serious Adverse Event \u2014 life threatening, requires hospitalization, or results in death' },
  { term: 'PK Sample', definition: 'Pharmacokinetic blood draw to measure drug concentration in plasma' },
  { term: 'ECG', definition: 'Electrocardiogram \u2014 measures heart electrical activity, monitors QTc interval' },
  { term: 'Vital Signs', definition: 'BP, HR, Temp, RR, SpO2' },
  { term: 'Lab Result', definition: 'Blood/urine laboratory tests' },
  { term: 'Cohort 1 / Cohort 2 / Cohort 3', definition: 'Patient groups receiving different dose levels in 3+3+3 escalation design' },
];

const EVENT_COLORS: Record<string, string> = {
  'Vital Signs': '#48bb78',
  'Lab Result': '#4299e1',
  'AE Report': '#ed8936',
  'SAE Report': '#f56565',
  'PK Sample': '#9f7aea',
  'ECG': '#38b2ac',
};

const COHORT_MAP: Record<string, { label: string; color: string }> = {
  'SYN-001': { label: 'Cohort 1 (10mg)', color: '#4299e1' },
  'SYN-002': { label: 'Cohort 1 (10mg)', color: '#4299e1' },
  'SYN-003': { label: 'Cohort 1 (10mg)', color: '#4299e1' },
  'SYN-004': { label: 'Cohort 2 (25mg)', color: '#9f7aea' },
  'SYN-005': { label: 'Cohort 2 (25mg)', color: '#9f7aea' },
  'SYN-006': { label: 'Cohort 2 (25mg)', color: '#9f7aea' },
  'SYN-007': { label: 'Cohort 3 (50mg)', color: '#ed8936' },
  'SYN-008': { label: 'Cohort 3 (50mg)', color: '#ed8936' },
  'SYN-009': { label: 'Cohort 3 (50mg)', color: '#ed8936' },
};

function getPatientId(subject: string): string {
  return subject.replace('Patient/', '');
}

type CohortFilter = 'all' | 'cohort1' | 'cohort2' | 'cohort3';

export default function EvidenceTimeline() {
  const [cohortFilter, setCohortFilter] = useState<CohortFilter>('all');

  const filteredEvents = useMemo(() => {
    if (cohortFilter === 'all') return DEMO_EVIDENCE_EVENTS;
    const prefix = cohortFilter === 'cohort1' ? 'Cohort 1' : cohortFilter === 'cohort2' ? 'Cohort 2' : 'Cohort 3';
    return DEMO_EVIDENCE_EVENTS.filter((ev) => {
      const pid = getPatientId(ev.subject);
      return COHORT_MAP[pid]?.label.startsWith(prefix);
    });
  }, [cohortFilter]);

  const data = useMemo(() => {
    const start = new Date('2026-01-15T00:00:00Z').getTime();
    return filteredEvents.map((ev) => {
      const pid = getPatientId(ev.subject);
      const cohort = COHORT_MAP[pid];
      return {
        ...ev,
        x: (new Date(ev.effectiveTimestamp).getTime() - start) / (1000 * 3600),
        y: (new Date(ev.internalKnowledgeTimestamp).getTime() - start) / (1000 * 3600),
        color: cohortFilter !== 'all' ? (cohort?.color || '#a0aec0') : (EVENT_COLORS[ev.eventType] || '#a0aec0'),
        cohortLabel: cohort?.label || 'Unknown',
        cohortColor: cohort?.color || '#a0aec0',
      };
    });
  }, [filteredEvents, cohortFilter]);

  const eventTypes = Array.from(new Set(DEMO_EVIDENCE_EVENTS.map((e) => e.eventType)));

  const { viewMode } = useViewMode();

  return (
    <div style={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1 }}>
          <h2 style={styles.title}>Evidence Timeline</h2>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <ViewModeToggle />
          <Glossary entries={EVIDENCE_GLOSSARY} />
        </div>
      </div>
      {viewMode === 'monitor' && <EvidenceMonitorPanel />}
      <p style={styles.desc}>
        Each point represents an evidence node. X-axis shows when the event occurred;
        Y-axis shows when it was recorded in the system. Points above the diagonal
        indicate recording delays.
      </p>
      <ComplianceBadges badges={["21 CFR Part 11", "ICH E6(R3)", "ALCOA"]} />

      {/* Cohort filter */}
      <div style={styles.filterRow}>
        <span style={styles.filterLabel}>Filter by Cohort:</span>
        {([
          { key: 'all' as CohortFilter, label: 'All' },
          { key: 'cohort1' as CohortFilter, label: 'Cohort 1 (10mg)' },
          { key: 'cohort2' as CohortFilter, label: 'Cohort 2 (25mg)' },
          { key: 'cohort3' as CohortFilter, label: 'Cohort 3 (50mg)' },
        ]).map((f) => (
          <button
            key={f.key}
            onClick={() => setCohortFilter(f.key)}
            style={{
              ...styles.filterBtn,
              backgroundColor: cohortFilter === f.key ? '#2b6cb0' : '#2d3748',
              color: cohortFilter === f.key ? '#fff' : '#a0aec0',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={styles.legend}>
        {cohortFilter !== 'all' ? (
          <>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: '#4299e1' }} />
              Cohort 1 (10mg)
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: '#9f7aea' }} />
              Cohort 2 (25mg)
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: '#ed8936' }} />
              Cohort 3 (50mg)
            </span>
          </>
        ) : (
          eventTypes.map((t) => (
            <span key={t} style={styles.legendItem}>
              <span style={{ ...styles.dot, backgroundColor: EVENT_COLORS[t] || '#a0aec0' }} />
              {t}
            </span>
          ))
        )}
      </div>
      <ResponsiveContainer width="100%" height={500}>
        <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 40 }}>
          <XAxis
            type="number" dataKey="x" name="Effective (hrs)"
            label={{ value: 'Effective Timestamp (hours from trial start)', position: 'bottom', offset: 20, style: { fill: '#a0aec0' } }}
            tick={{ fill: '#a0aec0' }}
          />
          <YAxis
            type="number" dataKey="y" name="Recorded (hrs)"
            label={{ value: 'Recorded Timestamp (hours)', angle: -90, position: 'insideLeft', offset: -10, style: { fill: '#a0aec0' } }}
            tick={{ fill: '#a0aec0' }}
          />
          <Tooltip
            content={({ payload }) => {
              if (!payload || !payload[0]) return null;
              const d = payload[0].payload;
              return (
                <div style={styles.tooltip}>
                  <div><strong>{d.id}</strong></div>
                  <div>Type: {d.eventType}</div>
                  <div>Subject: {d.subject}</div>
                  <div>Cohort: {d.cohortLabel}</div>
                  <div>Effective: {new Date(d.effectiveTimestamp).toLocaleString()}</div>
                  <div>Recorded: {new Date(d.internalKnowledgeTimestamp).toLocaleString()}</div>
                </div>
              );
            }}
          />
          <Scatter data={data} fill="#4299e1">
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
      <div style={styles.table}>
        <table style={styles.tableEl}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Type</th>
              <th style={styles.th}>Subject</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Cohort</th>
              <th style={styles.th}>Effective</th>
              <th style={styles.th}>Recorded</th>
              <th style={styles.th}>Delay</th>
              <th style={styles.th}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.map((ev) => {
              const delay = (new Date(ev.internalKnowledgeTimestamp).getTime() - new Date(ev.effectiveTimestamp).getTime()) / (1000 * 3600);
              const pid = getPatientId(ev.subject);
              const cohort = COHORT_MAP[pid];
              const site = SITE_MAP[pid];
              return (
                <tr key={ev.id}>
                  <td style={styles.td}>{ev.id}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.typeBadge, backgroundColor: EVENT_COLORS[ev.eventType] || '#a0aec0' }}>
                      {ev.eventType}
                    </span>
                  </td>
                  <td style={styles.td}>{ev.subject}</td>
                  <td style={styles.td}>
                    {site && <span style={{
                      ...styles.cohortBadge,
                      backgroundColor: site.color + '22',
                      color: site.color,
                      border: `1px solid ${site.color}`,
                    }}>{site.label}</span>}
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      ...styles.cohortBadge,
                      backgroundColor: cohort ? cohort.color + '22' : '#2d3748',
                      color: cohort?.color || '#a0aec0',
                      border: `1px solid ${cohort?.color || '#4a5568'}`,
                    }}>
                      {cohort?.label || 'Unknown'}
                    </span>
                  </td>
                  <td style={styles.td}>{new Date(ev.effectiveTimestamp).toLocaleString()}</td>
                  <td style={styles.td}>{new Date(ev.internalKnowledgeTimestamp).toLocaleString()}</td>
                  <td style={{ ...styles.td, color: delay > 12 ? '#f56565' : '#48bb78' }}>
                    {delay.toFixed(1)}h
                  </td>
                  <td style={{ ...styles.td, color: '#48bb78', fontWeight: 600 }}>
                    ${getEventCost(ev.eventType, site?.label || '').toLocaleString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div style={{ marginTop: 12, padding: '10px 16px', background: '#171923', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ color: '#a0aec0', fontSize: 13 }}>
            {filteredEvents.length} events recorded
          </span>
          <span style={{ color: '#48bb78', fontSize: 14, fontWeight: 700 }}>
            Estimated procedure cost: ${filteredEvents.reduce((sum, ev) => {
              const pid = getPatientId(ev.subject);
              const site = SITE_MAP[pid];
              return sum + getEventCost(ev.eventType, site?.label || '');
            }, 0).toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },
  filterRow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 },
  filterLabel: { color: '#a0aec0', fontSize: 13, fontWeight: 600 },
  filterBtn: {
    padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 12, fontWeight: 600, transition: 'all 0.15s',
  },
  legend: { display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' },
  legendItem: { display: 'flex', alignItems: 'center', gap: 6, color: '#cbd5e0', fontSize: 13 },
  dot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  tooltip: { backgroundColor: '#1a202c', border: '1px solid #2d3748', borderRadius: 8, padding: 12, color: '#e2e8f0', fontSize: 12 },
  table: { marginTop: 32, overflowX: 'auto' },
  tableEl: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 },
  typeBadge: { padding: '2px 8px', borderRadius: 12, color: '#fff', fontSize: 11, fontWeight: 600 },
  cohortBadge: { padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
};
